// Ch94 · WHAT A REEL IS CALLED — and why this is harder than it should be.
//
// 🔴 THE REAL BUILDING NAME DOES NOT ARRIVE. Traced 2026-09-19 at d7e9ac5:
//
//   LN-Workbench's mirror DOES emit it — contentMirror.ts:262-263 spreads
//   `property_name` and `property_id` into the review DTO.
//   LN Pro's repository NEVER READS IT — content_review_repository.py:244 selects
//   an EXPLICIT column list that omits both, and :346 builds ReviewItem without
//   them. So the field exists upstream, and is dropped on our side.
//
// That is why Grant sees `emmimidtown 28293` and `thehadley aes40`: with no real
// name, the card falls back to parsing the reel_id slug (ContentReviewCard.tsx:427
// `derivedName`). The slug is an IDENTIFIER, and identifiers are not language.
//
// ⚠️ WHY THIS IS NOT A ONE-LINE FIX, and must not be attempted as one:
// content_review_repository.py:247-250 carries its own warning — PostgREST answers
// an unknown column with 400/42703, nothing catches `postgrest.APIError`, and
// admin_content.py catches only ContentReviewStoreUnavailable/ValueError. So
// adding a column to that select BEFORE confirming it exists in prod takes the
// whole tab down. Whether `content_review_items.property_name` exists is a
// director_ro read; it is routed, not guessed.
//
// Until then this module makes the fallback as honest as it can be: it cleans the
// slug into words, and it NEVER pretends a cleaned slug is the building's name.

/** Only the fields a name can be built from. */
export interface NameableReel {
  reel_id: string;
  subject?: string;
  template?: string;
  /** Present only once the backend carries it through. Optional on purpose. */
  property_name?: string;
}

export interface ReelName {
  /** What to show. */
  text: string;
  /**
   * How we know it. The UI uses this to decide whether to present the name as
   * fact — a derived name gets a quieter treatment and an explanation, because
   * it is our guess at what an identifier meant.
   */
  source: "property" | "subject" | "derived" | "none";
}

/**
 * Clean a reel_id into words.
 *
 * Mirrors the existing `derivedName` (ContentReviewCard.tsx:427) rather than
 * inventing a second dialect: strip the template prefix, drop the trailing
 * content hash — identity, not language — and un-hyphenate.
 */
export function derivedFromReelId(reel_id: string, template?: string): string | null {
  const id = (reel_id || "").trim();
  const tpl = (template || "").trim();
  if (!id) return null;

  let rest = id;
  if (tpl && id.startsWith(`${tpl}-`)) rest = id.slice(tpl.length + 1);

  rest = rest.replace(/-[0-9a-f]{6,}$/i, "");
  rest = rest.replace(/^(service|property|neighborhood(-guide)?)-/, "");
  rest = rest.replace(/[-_]+/g, " ").trim();

  // A slug that cleans down to nothing, or to a bare number, is not a name.
  if (!rest || /^\d+$/.test(rest)) return null;
  return rest;
}

/**
 * The best name we can honestly give this reel.
 *
 * Order matters: a real property name outranks a human-written subject, which
 * outranks our own parse of an identifier.
 */
export function reelName(reel: NameableReel): ReelName {
  const property = (reel.property_name || "").trim();
  if (property) return { text: property, source: "property" };

  const subject = (reel.subject || "").trim();
  // "Untitled" is the translator's fallback, not a title someone chose.
  if (subject && subject.toLowerCase() !== "untitled") {
    return { text: subject, source: "subject" };
  }

  const derived = derivedFromReelId(reel.reel_id, reel.template);
  if (derived) return { text: derived, source: "derived" };

  return { text: "Building not recorded", source: "none" };
}

/**
 * The building to GROUP and FILTER by.
 *
 * `null` when we genuinely cannot tell — those cards get their own "Building not
 * recorded" option rather than being silently dropped from the filter, because a
 * card missing from a filter is a card missing from Grant's review.
 */
export function buildingKeyOf(reel: NameableReel): string | null {
  const name = reelName(reel);
  return name.source === "none" ? null : name.text;
}

/**
 * "2h", "6d" — how long since the row last changed.
 *
 * `null` when there is no timestamp, which is the normal state for a response
 * from before `updated_at` was carried through. A missing age shows NOTHING;
 * it never becomes "just now", which would be a claim about freshness we have
 * not got.
 */
export function ageLabel(iso: string | undefined, now: Date = new Date()): string | null {
  if (!iso) return null;
  const then = Date.parse(iso);
  if (!Number.isFinite(then)) return null;
  const hours = Math.floor((now.getTime() - then) / 3_600_000);
  // A future timestamp is a clock disagreement, not an age. Say nothing.
  if (hours < 0) return null;
  if (hours < 1) return "just now";
  if (hours < 24) return `${hours}h`;
  return `${Math.floor(hours / 24)}d`;
}

/** Past this, the mock colours the age — it has been waiting too long. */
export const STALE_AFTER_HOURS = 72;

export function isStale(iso: string | undefined, now: Date = new Date()): boolean {
  if (!iso) return false;
  const then = Date.parse(iso);
  return (
    Number.isFinite(then) && now.getTime() - then > STALE_AFTER_HOURS * 3_600_000
  );
}

/** The one-line explanation shown beside a name we inferred. */
export const DERIVED_NAME_NOTE =
  "Taken from the file name — the building's real name doesn't reach this screen yet.";
